import { fetchScreenerColumns, type ScreenerColumn } from "@/services/api/screener";
import { listAssetGroups } from "@/services/api/company-assets";
import {
  buildStaffFieldMentionId,
  DYNAMIC_BINDING_OPTIONS,
  getGroupFieldSchema,
  isPersonRolePool,
} from "@/services/api/company-assets/types";
import { useReportBuilderSafe } from "@/context/ReportBuilderContext";
import {
  DISCLAIMER_RESOLVED_CONTEXT_KEY,
  disclaimerPayloadToPlainText,
  pickDisclaimerPayloadFromRecord,
  readDisclaimerResolvedFromContext,
} from "@/utils/disclaimerPayloadFromRecord";

export interface DynamicValue {
  id: string;
  label: string;
  /** Optional category for grouping in the UI */
  category?: string;
  /** Optional path for API-powered values */
  path?: string;
  /** Optional source to hint where the value comes from */
  source?: string;
  /**
   * Return the string that should appear inside the editor when the mention is rendered.
   * You can pass extra context (for example, the actual page number at export time).
   */
  resolve: (context?: Record<string, any>) => string;
  /**
   * Optional placeholder token that will be kept in the HTML for late substitution (PDF export etc.).
   */
  placeholder?: string;
}

const toStartCase = (str: string = "") =>
  str
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b([a-z])/g, (m) => m.toUpperCase());

/** Available date format options for dynamic mentions */
export const DATE_FORMATS = [
  { id: "dd/mm/yyyy", label: "DD/MM/YYYY", example: "06/03/2026" },
  { id: "mm/dd/yyyy", label: "MM/DD/YYYY", example: "03/06/2026" },
  { id: "yyyy-mm-dd", label: "YYYY-MM-DD", example: "2026-03-06" },
  { id: "dd-mmm-yyyy", label: "DD-MMM-YYYY", example: "06-Mar-2026" },
  { id: "dd-month-yyyy", label: "DD Month YYYY", example: "06 March 2026" },
  { id: "month-yyyy", label: "Month YYYY", example: "March 2026" },
  { id: "mmm-yyyy", label: "MMM YYYY", example: "Mar 2026" },
] as const;

/** Predefined format IDs */
export type PredefinedDateFormatId = (typeof DATE_FORMATS)[number]["id"];

/** Date format can be a predefined ID or a custom format string */
export type DateFormatId = PredefinedDateFormatId | string;

/** Month names for date formatting */
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * Get the ordinal suffix for a day number (1st, 2nd, 3rd, etc.)
 */
function getOrdinalSuffix(day: number): string {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

/**
 * Available format tokens for custom date formats.
 * Exported for use in the help dialog.
 */
export const DATE_FORMAT_TOKENS = [
  { token: "YYYY", description: "4-digit year", example: "2026" },
  { token: "YY", description: "2-digit year", example: "26" },
  { token: "MMMM", description: "Full month name", example: "March" },
  { token: "MMM", description: "Abbreviated month", example: "Mar" },
  { token: "MM", description: "2-digit month", example: "03" },
  { token: "M", description: "Month number", example: "3" },
  { token: "DD", description: "2-digit day", example: "06" },
  { token: "D", description: "Day number", example: "6" },
  { token: "Do", description: "Day with ordinal", example: "6th" },
] as const;

/**
 * Token replacement functions for date formatting.
 * Keys are sorted by length (longest first) when building the regex to avoid partial matches.
 */
const FORMAT_TOKEN_MAP: Record<string, (d: Date) => string> = {
  YYYY: (d) => String(d.getFullYear()),
  YY: (d) => String(d.getFullYear()).slice(-2),
  MMMM: (d) => MONTH_NAMES[d.getMonth()],
  MMM: (d) => MONTH_NAMES[d.getMonth()].slice(0, 3),
  MM: (d) => String(d.getMonth() + 1).padStart(2, "0"),
  M: (d) => String(d.getMonth() + 1),
  Do: (d) => `${d.getDate()}${getOrdinalSuffix(d.getDate())}`,
  DD: (d) => String(d.getDate()).padStart(2, "0"),
  D: (d) => String(d.getDate()),
};

/**
 * Regex pattern to match all format tokens at once.
 * Tokens are sorted by length (longest first) to ensure proper matching.
 * Uses word boundaries to avoid matching tokens within longer words.
 * The negative lookbehind (?<![A-Za-z]) ensures tokens don't match after letters.
 * The negative lookahead (?![a-z]) ensures tokens don't match before lowercase letters.
 */
const FORMAT_TOKEN_REGEX = new RegExp(
  "(?<![A-Za-z])(" +
    Object.keys(FORMAT_TOKEN_MAP)
      .sort((a, b) => b.length - a.length)
      .join("|") +
    ")(?![a-z])",
  "g"
);

/**
 * Check if a format string is a predefined format ID.
 */
function isPredefinedFormat(format: string): format is PredefinedDateFormatId {
  return DATE_FORMATS.some((f) => f.id === format);
}

/**
 * Apply custom format tokens to a date.
 * Replaces tokens like YYYY, MM, DD with actual values.
 * Uses a single regex pass to avoid partial token matches.
 */
function applyCustomFormat(date: Date, format: string): string {
  return format.replace(FORMAT_TOKEN_REGEX, (match) => {
    const replacer = FORMAT_TOKEN_MAP[match];
    return replacer ? replacer(date) : match;
  });
}

/**
 * Format a date value according to the specified format.
 * Supports both predefined format IDs and custom format strings with tokens.
 *
 * @param value - The date value to format (Date, string, or number)
 * @param format - A predefined format ID or custom format string (defaults to "dd/mm/yyyy")
 * @returns Formatted date string
 *
 * @example
 * // Predefined formats
 * formatDateWithFormat(new Date(), "dd/mm/yyyy") // "06/03/2026"
 * formatDateWithFormat(new Date(), "month-yyyy") // "March 2026"
 *
 * // Custom formats
 * formatDateWithFormat(new Date(), "MMMM Do, YYYY") // "March 6th, 2026"
 * formatDateWithFormat(new Date(), "DD-MM-YY") // "06-03-26"
 */
export function formatDateWithFormat(
  value: any,
  format: DateFormatId = "dd/mm/yyyy"
): string {
  if (!value) return "";

  try {
    let date: Date;

    if (value instanceof Date) {
      date = value;
    } else if (typeof value === "string" || typeof value === "number") {
      date = new Date(value);
    } else {
      return String(value);
    }

    if (isNaN(date.getTime())) {
      return String(value);
    }

    // Handle predefined formats for backwards compatibility
    if (isPredefinedFormat(format)) {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const monthShort = MONTH_NAMES[date.getMonth()].slice(0, 3);
      const monthFull = MONTH_NAMES[date.getMonth()];

      switch (format) {
        case "dd/mm/yyyy":
          return `${day}/${month}/${year}`;
        case "mm/dd/yyyy":
          return `${month}/${day}/${year}`;
        case "yyyy-mm-dd":
          return `${year}-${month}-${day}`;
        case "dd-mmm-yyyy":
          return `${day}-${monthShort}-${year}`;
        case "dd-month-yyyy":
          return `${day} ${monthFull} ${year}`;
        case "month-yyyy":
          return `${monthFull} ${year}`;
        case "mmm-yyyy":
          return `${monthShort} ${year}`;
      }
    }

    // Apply custom format tokens
    return applyCustomFormat(date, format);
  } catch (e) {
    console.error("Error formatting date:", e);
    return String(value);
  }
}

export let dynamicValues: DynamicValue[] = [
  {
    id: "date",
    label: "Today's date",
    category: "General",
    resolve: (context?: Record<string, any>) => {
      const date = new Date();
      const dateFormat = context?.dateFormat as DateFormatId | undefined;

      // If a specific date format is requested, use it
      if (dateFormat) {
        return formatDateWithFormat(date, dateFormat);
      }

      // Default to the original "DD Month, YYYY" format
      const options: Intl.DateTimeFormatOptions = {
        day: "2-digit",
        month: "long",
        year: "numeric",
      };
      try {
        const formatter = new Intl.DateTimeFormat("en-GB", options);
        const parts = formatter.formatToParts(date);
        const day = parts.find((p) => p.type === "day")?.value;
        const month = parts.find((p) => p.type === "month")?.value;
        const year = parts.find((p) => p.type === "year")?.value;

        if (day && month && year) {
          return `${day} ${month}, ${year}`;
        }
      } catch (e) {
        console.error("Could not format date with Intl.DateTimeFormat", e);
      }
      // Fallback for safety
      return new Date().toLocaleDateString("en-GB"); // e.g. 28/07/2025
    },
  },
  {
    id: "saltus_disclaimers",
    label: "Disclaimers",
    category: "General",
    resolve: (context?: Record<string, any>) => {
      const raw = readDisclaimerResolvedFromContext(
        context as Record<string, unknown> | undefined
      );
      const text = disclaimerPayloadToPlainText(raw);
      return text.trim() ? text : "Saltus Disclaimers";
    },
  },
  {
    id: "disclaimers_count",
    label: "Disclaimers Count",
    category: "General",
    resolve: (context?: Record<string, any>) => {
      const raw = readDisclaimerResolvedFromContext(
        context as Record<string, unknown> | undefined
      );
      const contentText = disclaimerPayloadToPlainText(raw);
      const disclaimerNumbers: string[] = [];

      // Find all numbered references like (1), (2), (3), etc.
      const numberMatches = contentText.match(/\((\d+)\)/g);
      if (numberMatches) {
        numberMatches.forEach((match) => {
          const number = match.replace(/[()]/g, "");
          if (!disclaimerNumbers.includes(number)) {
            disclaimerNumbers.push(number);
          }
        });
      }

      // Sort numbers numerically and return with parentheses (empty string if none found)
      disclaimerNumbers.sort((a, b) => parseInt(a) - parseInt(b));
      return disclaimerNumbers.length > 0
        ? disclaimerNumbers.map((num) => `(${num})`).join("")
        : "";
    },
  },
  {
    id: "date_month_year",
    label: "Month Year",
    category: "Date & Time",
    resolve: () => {
      const date = new Date();
      try {
        const formatter = new Intl.DateTimeFormat("en-GB", {
          month: "long",
          year: "numeric",
        });
        const parts = formatter.formatToParts(date);
        const month = parts.find((p) => p.type === "month")?.value;
        const year = parts.find((p) => p.type === "year")?.value;
        if (month && year) return `${month} ${year}`; // e.g. July 2025
      } catch {
        // ignore
      }
      // Fallback
      const m = date.toLocaleString("en-GB", { month: "long" });
      return `${m} ${date.getFullYear()}`;
    },
  },
  {
    id: "date_iso",
    label: "YYYY-MM-DD",
    category: "Date & Time",
    resolve: () => new Date().toISOString().slice(0, 10),
  },
  {
    id: "as_at_date",
    label: "Report Date (DD/MM/YYYY)",
    category: "Date & Time",
    resolve: (context?: Record<string, any>) => {
      // Get as_at from context, fallback to today
      const asAtString = context?.as_at;
      const date = asAtString ? new Date(asAtString) : new Date();
      const dateFormat = context?.dateFormat as DateFormatId | undefined;

      // If a specific date format is requested, use it
      if (dateFormat) {
        return formatDateWithFormat(date, dateFormat);
      }

      try {
        // Default format: DD/MM/YYYY (e.g. 25/09/2025)
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      } catch (e) {
        console.error("Could not format as_at date", e);
      }
      // Fallback
      return date.toLocaleDateString("en-GB");
    },
  },
  {
    id: "as_at_month_year",
    label: "Report Date (Month YYYY)",
    category: "Date & Time",
    resolve: (context?: Record<string, any>) => {
      const asAtString = context?.as_at;
      const date = asAtString ? new Date(asAtString) : new Date();
      const dateFormat = context?.dateFormat as DateFormatId | undefined;

      // If a specific date format is requested, use it
      if (dateFormat) {
        return formatDateWithFormat(date, dateFormat);
      }

      try {
        const formatter = new Intl.DateTimeFormat("en-GB", {
          month: "long",
          year: "numeric",
        });
        const parts = formatter.formatToParts(date);
        const month = parts.find((p) => p.type === "month")?.value;
        const year = parts.find((p) => p.type === "year")?.value;
        if (month && year) return `${month} ${year}`; // e.g. September 2025
      } catch {
        // ignore
      }
      // Fallback
      const m = date.toLocaleString("en-GB", { month: "long" });
      return `${m} ${date.getFullYear()}`;
    },
  },
  {
    id: "as_at_iso",
    label: "Report Date (YYYY-MM-DD)",
    category: "Date & Time",
    resolve: (context?: Record<string, any>) => {
      const asAtString = context?.as_at;
      const date = asAtString ? new Date(asAtString) : new Date();
      const dateFormat = context?.dateFormat as DateFormatId | undefined;

      // If a specific date format is requested, use it
      if (dateFormat) {
        return formatDateWithFormat(date, dateFormat);
      }

      if (asAtString) {
        // If it's already in ISO format, return as is
        if (/^\d{4}-\d{2}-\d{2}$/.test(asAtString)) {
          return asAtString;
        }
        // Otherwise parse and format
        return new Date(asAtString).toISOString().slice(0, 10);
      }
      return new Date().toISOString().slice(0, 10);
    },
  },
  {
    id: "time_hm",
    label: "Time (HH:mm)",
    category: "Date & Time",
    resolve: () => {
      const d = new Date();
      const hh = String(d.getHours()).padStart(2, "0");
      const mm = String(d.getMinutes()).padStart(2, "0");
      return `${hh}:${mm}`;
    },
  },
  {
    id: "pageNumber",
    label: "Page Number",
    category: "General",
    resolve: () => "Page Number",
    placeholder: "Page Number",
  },
  // Time Period Start Dates
  {
    id: "period_1m_start",
    label: "1 Month",
    category: "Time Period Start Dates",
    resolve: (context?: Record<string, any>) => {
      const asAtString = context?.as_at;
      const reportDate = asAtString ? new Date(asAtString) : new Date();

      try {
        return calculateTimePeriodStartDate(reportDate, "1M");
      } catch (e) {
        console.error("Could not calculate 1M start date", e);
        return calculateTimePeriodStartDate(new Date(), "1M");
      }
    },
  },
  {
    id: "period_3m_start",
    label: "3 Months",
    category: "Time Period Start Dates",
    resolve: (context?: Record<string, any>) => {
      const asAtString = context?.as_at;
      const reportDate = asAtString ? new Date(asAtString) : new Date();

      try {
        return calculateTimePeriodStartDate(reportDate, "3M");
      } catch (e) {
        console.error("Could not calculate 3M start date", e);
        return calculateTimePeriodStartDate(new Date(), "3M");
      }
    },
  },
  {
    id: "period_6m_start",
    label: "6 Months",
    category: "Time Period Start Dates",
    resolve: (context?: Record<string, any>) => {
      const asAtString = context?.as_at;
      const reportDate = asAtString ? new Date(asAtString) : new Date();

      try {
        return calculateTimePeriodStartDate(reportDate, "6M");
      } catch (e) {
        console.error("Could not calculate 6M start date", e);
        return calculateTimePeriodStartDate(new Date(), "6M");
      }
    },
  },
  {
    id: "period_ytd_start",
    label: "YTD",
    category: "Time Period Start Dates",
    resolve: (context?: Record<string, any>) => {
      const asAtString = context?.as_at;
      const reportDate = asAtString ? new Date(asAtString) : new Date();

      try {
        return calculateTimePeriodStartDate(reportDate, "YTD");
      } catch (e) {
        console.error("Could not calculate YTD start date", e);
        return calculateTimePeriodStartDate(new Date(), "YTD");
      }
    },
  },
  {
    id: "period_1y_start",
    label: "1 Year",
    category: "Time Period Start Dates",
    resolve: (context?: Record<string, any>) => {
      const asAtString = context?.as_at;
      const reportDate = asAtString ? new Date(asAtString) : new Date();

      try {
        return calculateTimePeriodStartDate(reportDate, "1Y");
      } catch (e) {
        console.error("Could not calculate 1Y start date", e);
        return calculateTimePeriodStartDate(new Date(), "1Y");
      }
    },
  },
  {
    id: "period_3y_start",
    label: "3 Years",
    category: "Time Period Start Dates",
    resolve: (context?: Record<string, any>) => {
      const asAtString = context?.as_at;
      const reportDate = asAtString ? new Date(asAtString) : new Date();

      try {
        return calculateTimePeriodStartDate(reportDate, "3Y");
      } catch (e) {
        console.error("Could not calculate 3Y start date", e);
        return calculateTimePeriodStartDate(new Date(), "3Y");
      }
    },
  },
  {
    id: "period_5y_start",
    label: "5 Years",
    category: "Time Period Start Dates",
    resolve: (context?: Record<string, any>) => {
      const asAtString = context?.as_at;
      const reportDate = asAtString ? new Date(asAtString) : new Date();

      try {
        return calculateTimePeriodStartDate(reportDate, "5Y");
      } catch (e) {
        console.error("Could not calculate 5Y start date", e);
        return calculateTimePeriodStartDate(new Date(), "5Y");
      }
    },
  },
  {
    id: "period_10y_start",
    label: "10 Years",
    category: "Time Period Start Dates",
    resolve: (context?: Record<string, any>) => {
      const asAtString = context?.as_at;
      const reportDate = asAtString ? new Date(asAtString) : new Date();

      try {
        return calculateTimePeriodStartDate(reportDate, "10Y");
      } catch (e) {
        console.error("Could not calculate 10Y start date", e);
        return calculateTimePeriodStartDate(new Date(), "10Y");
      }
    },
  },
];

/**
 * Inject API-provided dynamic values into the mention picker.
 * This preserves existing static values while allowing field definition items to be added
 * or refreshed at runtime.
 */
export function setApiDynamicValues(values: DynamicValue[]) {
  // Keep built-ins at the top, then append/replace API values (by id)
  const builtIns = dynamicValues.filter(
    (v) => v.category === "General" || v.category === "Date & Time"
  );
  const others = dynamicValues.filter(
    (v) => v.category !== "General" && v.category !== "Date & Time"
  );

  // Merge by id: API values should replace any previous API-injected items with the same id
  const apiMap = new Map(values.map((v) => [v.id, v] as const));
  const mergedOthers = others.filter((v) => !apiMap.has(v.id)).concat(values);

  // Remove any duplicates by id (keep the first occurrence - built-ins first)
  const allValues = [...builtIns, ...mergedOthers];
  const uniqueValues = new Map();
  allValues.forEach((value) => {
    if (!uniqueValues.has(value.id)) {
      uniqueValues.set(value.id, value);
    }
  });

  dynamicValues = Array.from(uniqueValues.values());
}

/**
 * Format a date value to DD/MM/YYYY format
 */
/**
 * Format a date value to the specified format (defaults to DD/MM/YYYY).
 * @param value - The date value to format
 * @param format - Optional format ID from DATE_FORMATS
 */
function formatDateValue(value: any, format?: DateFormatId): string {
  return formatDateWithFormat(value, format || "dd/mm/yyyy");
}

/**
 * Calculate time period start date based on report date and time period
 * Uses the same logic as chart time period calculations
 */
function calculateTimePeriodStartDate(
  reportDate: Date,
  timePeriod: string
): string {
  const endDate = new Date(reportDate);
  const startDate = new Date(endDate);

  switch (timePeriod) {
    case "1M":
      startDate.setMonth(endDate.getMonth() - 1);
      break;
    case "3M":
      startDate.setMonth(endDate.getMonth() - 3);
      break;
    case "6M":
      startDate.setMonth(endDate.getMonth() - 6);
      break;
    case "YTD":
      startDate.setMonth(0, 1); // January 1st of current year
      break;
    case "1Y":
      startDate.setFullYear(endDate.getFullYear() - 1);
      break;
    case "3Y":
      startDate.setFullYear(endDate.getFullYear() - 3);
      break;
    case "5Y":
      startDate.setFullYear(endDate.getFullYear() - 5);
      break;
    case "10Y":
      startDate.setFullYear(endDate.getFullYear() - 10);
      break;
    default:
      // Default to 5Y if unknown period
      startDate.setFullYear(endDate.getFullYear() - 5);
  }

  // Format as DD/MM/YYYY
  const day = String(startDate.getDate()).padStart(2, "0");
  const month = String(startDate.getMonth() + 1).padStart(2, "0");
  const year = startDate.getFullYear();

  return `${day}/${month}/${year}`;
}

export async function initializeStaffFieldDynamicValues() {
  try {
    const groups = await listAssetGroups();
    const staffValues: DynamicValue[] = [];

    for (const group of groups) {
      if (!isPersonRolePool(group.role_pool)) {
        continue;
      }
      const fieldSchema = getGroupFieldSchema(group);
      for (const binding of DYNAMIC_BINDING_OPTIONS) {
        if (binding.defaultGroupRole !== group.role_pool) {
          continue;
        }
        for (const field of fieldSchema) {
          const mentionId = buildStaffFieldMentionId(
            binding.value,
            group._id,
            field.key
          );
          staffValues.push({
            id: mentionId,
            label: `${group.name}, ${field.label}`,
            category: "Team",
            resolve: () => `${group.name}, ${field.label}`,
            placeholder: mentionId,
          });
        }
      }
    }

    dynamicValues = dynamicValues.filter((v) => !v.id.startsWith("staff_field:"));
    if (staffValues.length > 0) {
      dynamicValues = [...dynamicValues, ...staffValues];
    }
  } catch (error) {
    console.error("Failed to initialize staff field dynamic values:", error);
  }
}

export async function initializeDynamicValues() {
  try {
    await initializeStaffFieldDynamicValues();
    const columns: ScreenerColumn[] = await fetchScreenerColumns();
    const apiValues: DynamicValue[] = [];

    columns.forEach((col) => {
      const displayLabel = toStartCase(col.name || col.id);
      // Skip any columns that might conflict with our built-in disclaimers
      if (
        col.id === "disclaimers" ||
        col.id === "saltus_disclaimers" ||
        col.id === "disclaimers_count"
      ) {
        return;
      }

      // Check if this is a date field (ends with _date or has date-like type)
      const isDateField =
        col.id.endsWith("_date") ||
        (col as any).type === "date" ||
        (col as any).format === "date";

      // Keep compatibility by using the "field:" prefix and leverage category/path hints
      apiValues.push({
        id: `field:${col.id}`,
        label: displayLabel,
        category: String(
          (col as any).source || (col as any).group || "General"
        ),
        path: (col as any).field_path || null,
        source: (col as any).source || undefined,
        resolve: (context?: Record<string, any>) => {
          // Check if we have a specific resource slot to resolve from
          const slotId = context?.resourceSlotId;
          const selectedResources = context?.selected_resources;
          const dateFormat = context?.dateFormat as DateFormatId | undefined;

          // If we have slot-specific resource data, try to resolve from it
          if (slotId && selectedResources) {
            const resourceForSlot = selectedResources[slotId];
            if (resourceForSlot && resourceForSlot.data && col.id in resourceForSlot.data) {
              const value = resourceForSlot.data[col.id];
              return isDateField ? formatDateValue(value, dateFormat) : String(value || "");
            }
          }

          // Try first resource slot as default (backwards compatibility)
          if (selectedResources && !slotId) {
            const firstSlotId = Object.keys(selectedResources)[0];
            if (firstSlotId) {
              const resourceForSlot = selectedResources[firstSlotId];
              if (resourceForSlot && resourceForSlot.data && col.id in resourceForSlot.data) {
                const value = resourceForSlot.data[col.id];
                return isDateField ? formatDateValue(value, dateFormat) : String(value || "");
              }
            }
          }

          // Legacy: If context has the actual value directly, format it (especially for dates)
          if (context && col.id in context) {
            const value = context[col.id];
            return isDateField ? formatDateValue(value, dateFormat) : String(value || "");
          }

          // Fallback to display label (placeholder in builder)
          return displayLabel;
        },
        placeholder: col.id,
      });
    });

    setApiDynamicValues(apiValues);
  } catch (error) {
    console.error(
      "Failed to initialize dynamic values from screener columns:",
      error
    );
  }
}

export const findDynamicValue = (id: string, values?: DynamicValue[]) =>
  (values || dynamicValues).find((d) => d.id === id);

/**
 * Get dynamic values without React hooks.
 * Safe to call from non-React contexts (like TipTap renderHTML).
 *
 * @param contextData Optional merged report fields (disclaimer keys coalesced by
 *   `pickDisclaimerPayloadFromRecord`, plus `as_at`, `resource_slots`, `selected_resources`)
 * @param resourceSlotId Optional resource slot ID to resolve field values from a specific slot
 */
export const getDynamicValues = (contextData?: {
  saltus_disclaimers?: any;
  saltus_birchwood_disclaimer?: any;
  saltus_nlp_disclaimer?: any;
  as_at?: string | null;
  resource_slots?: Array<{
    id: string;
    label: string;
    supported_types: string[];
    required: boolean;
  }>;
  selected_resources?: Record<string, { name: string; type: string }>;
  resourceSlotId?: string | null;
}): DynamicValue[] => {
  const disclaimerPayloadFromContext =
    pickDisclaimerPayloadFromRecord(
      contextData as Record<string, unknown> | undefined
    ) ?? null;
  const asAtDate = contextData?.as_at || null;
  const resourceSlots = contextData?.resource_slots || [];
  const selectedResources = contextData?.selected_resources || {};
  const resourceSlotId = contextData?.resourceSlotId || null;

  // Create resource slot dynamic values
  const resourceSlotValues: DynamicValue[] = resourceSlots.map((slot) => ({
    id: `resource_slot:${slot.id}`,
    label: slot.label,
    category: "Resources",
    resolve: (context?: Record<string, any>) => {
      // Check if we have a selected resource for this slot
      const contextResources = context?.selected_resources || selectedResources;
      const selectedResource = contextResources[slot.id];

      if (selectedResource?.name) {
        return selectedResource.name;
      }

      // Fallback to slot label for display in builder
      return slot.label;
    },
    placeholder: `resource_slot_${slot.id}`,
  }));

  // Combine base dynamic values with resource slot values
  const allValues = [...dynamicValues, ...resourceSlotValues];

  // Return dynamic values with context-aware resolve functions
  return allValues.map((value) => {
    // Full disclaimer body + derived footnote index list
    if (value.id === "saltus_disclaimers" || value.id === "disclaimers_count") {
      return {
        ...value,
        resolve: (context?: Record<string, any>) => {
          const resolvedPayload =
            readDisclaimerResolvedFromContext(
              context as Record<string, unknown> | undefined
            ) ??
            pickDisclaimerPayloadFromRecord(
              context as Record<string, unknown> | undefined
            ) ??
            disclaimerPayloadFromContext;

          if (resolvedPayload == null || resolvedPayload === "") {
            return value.id === "saltus_disclaimers"
              ? "Saltus Disclaimers"
              : "";
          }

          try {
            return value.resolve({
              [DISCLAIMER_RESOLVED_CONTEXT_KEY]: resolvedPayload,
            } as Record<string, any>);
          } catch (error) {
            console.warn(`Error resolving ${value.id}:`, error);
            return value.id === "saltus_disclaimers"
              ? "Saltus Disclaimers"
              : "";
          }
        },
      };
    }

    // Handle as_at date mentions
    if (
      value.id === "as_at_date" ||
      value.id === "as_at_month_year" ||
      value.id === "as_at_iso"
    ) {
      return {
        ...value,
        resolve: (context?: Record<string, any>) => {
          // Use provided context first, fall back to hook context
          const contextAsAt = context?.as_at || asAtDate;

          try {
            return value.resolve({ as_at: contextAsAt });
          } catch (error) {
            console.warn(`Error resolving ${value.id}:`, error);
            // Fallback to today's date
            return value.resolve({});
          }
        },
      };
    }

    // Handle time period start date mentions
    if (
      value.id === "period_1m_start" ||
      value.id === "period_3m_start" ||
      value.id === "period_6m_start" ||
      value.id === "period_ytd_start" ||
      value.id === "period_1y_start" ||
      value.id === "period_3y_start" ||
      value.id === "period_5y_start" ||
      value.id === "period_10y_start"
    ) {
      return {
        ...value,
        resolve: (context?: Record<string, any>) => {
          // Use provided context first, fall back to hook context
          const contextAsAt = context?.as_at || asAtDate;

          try {
            return value.resolve({ as_at: contextAsAt });
          } catch (error) {
            console.warn(`Error resolving ${value.id}:`, error);
            // Fallback to today's date
            return value.resolve({});
          }
        },
      };
    }

    return value;
  });
};

/**
 * React hook to get dynamic values with context from report builder.
 * Use getDynamicValues() instead if you need to call this from non-React contexts.
 */
export const useDynamicValues = (): DynamicValue[] => {
  // Call hook unconditionally (Rules of Hooks) - returns null if context not available
  const context = useReportBuilderSafe();

  const asAtDate = context
    ? (context.currentReport?.data as any)?.as_at || null
    : null;

  const resourceSlots = context?.resourceSlots || [];

  const selectedResources = context
    ? (context.currentReport?.data as any)?.selected_resources || {}
    : {};

  // Use the non-hook version with the context data we retrieved
  return getDynamicValues({
    ...((context?.currentReport?.data as Record<string, unknown>) || {}),
    as_at: asAtDate,
    resource_slots: resourceSlots,
    selected_resources: selectedResources,
  });
};
